import { memo, useCallback } from 'react';
import { useMount, useToggle } from '@cutwise/react-utils';

import locationReload from '@/common/helpers/locationReload';
import { HttpStatus } from '@/common/network/constants/httpStatus';
import { securityManager } from '@/common/services/SecurityManager';
import { AuthModalConfirmation } from '@/user/components/AuthModal/AuthModal';

import Button from '../../components/Button/Button';
import Layout from '../../components/Layout/Layout';
import Link from '../../components/Link/Link';
import AppError, { IMG_COMMON, IMG_COMPARISON, IMG_PRIVACY } from '../../state/AppError';

import commonImg from './common.svg';
import comparisonImg from './comparison.svg';
import styles from './ErrorPage.module.css';
import privacyImg from './privacy.svg';

const IMAGES = {
  [IMG_COMMON]: commonImg,
  [IMG_COMPARISON]: comparisonImg,
  [IMG_PRIVACY]: privacyImg,
};

interface ErrorPageProps {
  error: AppError;
  isReloadPageButtonShown?: boolean;
}

function ErrorPage(props: ErrorPageProps) {
  const { error, isReloadPageButtonShown } = props;

  const [shouldRenderPage, renderPage] = useToggle();

  const onLoginHandler = useCallback(() => AuthModalConfirmation(), []);

  const onReloadPageHandler = useCallback(() => locationReload(), []);

  useMount(() => {
    if (
      securityManager.isAnonymous() &&
      (error.code === HttpStatus.Forbidden || error.code === HttpStatus.Unauthorized)
    ) {
      AuthModalConfirmation({ onAfterClose: renderPage });
    } else {
      renderPage();
    }
  });

  if (!shouldRenderPage) {
    return null;
  }

  return (
    <Layout>
      <div className={styles.pageErrorWrapper}>
        <div className={styles.pageError}>
          {error.code === HttpStatus.NotFound && (
            <div className={styles.pageErrorCode} aria-label="Error Code">
              404
            </div>
          )}

          <div className={styles.pageErrorTitle} aria-label="Error Title">
            <h2>{error.title}</h2>
          </div>

          <div className={styles.pageErrorImage} aria-label="Error Image">
            <img alt={error.imgName} src={IMAGES[error.imgName]} />
          </div>

          <div className={styles.pageErrorDescription} aria-label="Error Description">
            <div>{error.description}</div>

            <div className={styles.pageErrorDescriptionLinks} aria-label="Error Links">
              {error.hasLinks && (
                <div className={styles.pageErrorDescriptionHasLinks}>
                  Let&apos;s get you back on track with helpful link(s):
                </div>
              )}

              {error.hasCatalogLink && (
                <div className={styles.center}>
                  Go to the <Link aria-label="Catalog page" to="/catalog">Catalog page</Link>
                </div>
              )}

              {error.hasCollectionLink && (
                <div className={styles.center}>
                  <Link aria-label="View Demo" to="/collections">View Demo</Link>
                </div>
              )}

              {error.hasLoginLink && (
                <div className={styles.center}>
                  <Button aria-label="Log in" onClick={onLoginHandler}>Log in</Button>
                </div>
              )}

              {isReloadPageButtonShown && (
                <div className={styles.center}>
                  <Button aria-label="Try again" onClick={onReloadPageHandler}>
                    Try again
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default memo(ErrorPage);
